import React, { Component } from 'react'
import { connect } from "react-redux";
import { Button, Icon, Message, Modal, Table } from 'semantic-ui-react'
import { sendDrPayReport, sendOrthoDrPayReport, checkOmittedNotes } from "../../redux/modules/pay";
import moment from "moment/moment";
import { defaultPrefix } from '../../utils/pay-utils';
import { copyObject } from '../../redux/modules/aws-bucket';

// numeric role ids (from sessions API Permissions.roleid)
const ROLE_IDS = {
    'ortho-pay-approval': 67108864,
    'general-pay-approval': 134217728
}

class EarningSending extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showSendModal: false,
            success: false,
            checkingNotes: false,
            noteCheck: null,
            noteCheckError: null
        }
    }

    openSendModal = () => this.setState({ showSendModal: true, checkingNotes: false, noteCheck: null, noteCheckError: null });

    // Warning only - never blocks a send. Runs only when the user explicitly asks for it.
    handleCheckOmittedNotes = async () => {
        const { selectedDoctors, startDate, endDate, source, saved } = this.props;
        this.setState({ checkingNotes: true, noteCheck: null, noteCheckError: null });
        try {
            const result = await this.props.dispatch(checkOmittedNotes(
                { startDate, endDate, source: source || null, selectedDoctors },
                { saved }
            ));
            this.setState({ checkingNotes: false, noteCheck: result || { omitted: [] } });
        } catch (err) {
            this.setState({ checkingNotes: false, noteCheckError: (err && err.message) || 'Could not run the check.' });
        }
    }

    renderNoteCheck = () => {
        const { checkingNotes, noteCheck, noteCheckError } = this.state;
        if (checkingNotes) return <Message icon info><Icon name='circle notched' loading /><Message.Content>Comparing the saved report with live notes...</Message.Content></Message>;
        if (noteCheckError) return <Message warning content={`Note check failed (you can still send): ${noteCheckError}`} />;
        if (!noteCheck) return null;

        const omitted = noteCheck.omitted || [];
        const sourceLabel = noteCheck.sourceKey
            ? `${noteCheck.sourceType === 'locked' ? 'Locked' : 'Latest'} report${noteCheck.reportGeneratedAt ? `, generated ${moment(noteCheck.reportGeneratedAt).format('MMM D, YYYY h:mm a')}` : ''}`
            : null;

        if (!omitted.length) {
            return <Message positive>
                <Message.Header>No omitted notes found</Message.Header>
                <p>{noteCheck.message || `Every dated note for the selected providers is already in the report this send reads.`}</p>
                {sourceLabel && <small>{sourceLabel}</small>}
            </Message>;
        }

        return <Message warning>
            <Message.Header>{omitted.length} note{omitted.length === 1 ? '' : 's'} not reflected in this report</Message.Header>
            <p>This send reads a saved report. These notes are newer than it, or missing from it, so provider pay may not include them. Refresh the report (or unlock/re-lock it) before sending, or send as is.</p>
            {sourceLabel && <small>{sourceLabel}</small>}
            <div style={{ maxHeight: 260, overflow: 'auto', marginTop: 8 }}>
                <Table compact size='small' basic='very'>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Provider</Table.HeaderCell>
                            <Table.HeaderCell>Practice</Table.HeaderCell>
                            <Table.HeaderCell>Note date</Table.HeaderCell>
                            <Table.HeaderCell textAlign='right'>Amount</Table.HeaderCell>
                            <Table.HeaderCell>Why</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {omitted.map(n => (
                            <Table.Row key={`${n.id}-${n.reason}`}>
                                <Table.Cell>{n.provider}</Table.Cell>
                                <Table.Cell>{n.practice || '-'}</Table.Cell>
                                <Table.Cell>{n.date}</Table.Cell>
                                <Table.Cell textAlign='right'>{Number(n.amount).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</Table.Cell>
                                <Table.Cell title={n.title}>{n.detail}</Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </div>
        </Message>;
    }

    closeSendModal = () => this.setState({ showSendModal: false });

    handleSendReport = (isTest = false) => {
        const { selectedDoctors, startDate, endDate, payValidation, currentPeriod, source, sendToProviders, saved } = this.props;

        if (!selectedDoctors.length) {
            alert('Please select at-least one provider!');
            this.closeSendModal();
            return;
        }

        let obj = {
            abbr: selectedDoctors,
            startDate: startDate,
            endDate: endDate,
            providerValidationRecords: payValidation,
            isTest,
        };

        if (currentPeriod != null) {
            obj = { ...obj, ...currentPeriod };
        }

        if (this.props.type === 'cdpOrthoReports') {
            this.props.dispatch(sendOrthoDrPayReport(obj));
        } else {
            let sendPayProps = {
                source: source ? source : null,
                startDate: startDate,
                endDate: endDate,
                selectedDoctors: selectedDoctors,
                providerValidationRecords: payValidation,
                abbr: false,
                isTest,
                "filters": {
                    "dentistType": 2,
                    "startDate": startDate,
                    "endDate": endDate,
                    "years": moment(startDate).format('YYYY'),
                    "months": [moment(startDate).format('MM')],
                    "keyword": "",
                    "dentist": false
                },
                "sortBy": {},
                lock: {
                    action: null
                }
            };

            if (source === 'PayCalendarCareTopMenus') {
                sendPayProps.postedFrom = 'PCMS' // pay calendar manual send
            }

            // Add currentPeriod to sendPayProps if available
            if (currentPeriod != null) {
                sendPayProps = { ...sendPayProps, currentPeriod };
            }

            this.props.dispatch(sendDrPayReport(sendPayProps, { saved: this.props.saved }));
            if (sendToProviders && saved) {
                const date = moment(startDate).format('MM-YYYY');
                this.props.dispatch(copyObject({ key: `${defaultPrefix}/${date}.gz`, source: `pay-data/${date}.gz` }));
            }
        }

        this.setState({ showSendModal: false, success: true });
    }

    normalizeRole = (s) => {
        return s ? s.toString().toLowerCase().replace(/[^a-z0-9]/g, '') : '';
    }

    hasUdrRole = (roleKey) => {
        const { user } = this.props;
        if (!user) return false;

        // first check numeric Permissions array (if role is defined as numeric id)
        const numericId = ROLE_IDS[roleKey];
        if (numericId && Array.isArray(user.Permissions) && user.Permissions.length) {
            const found = user.Permissions.some(p => {
                if (!p) return false;
                // roleid may be string or number
                const rid = p.roleid !== undefined ? p.roleid : p.roleId;
                if (rid === undefined || rid === null) return false;
                return Number(rid) === Number(numericId);
            });
            if (found) return true;
        }

        // fallback to UdrUsersPermissions textual roles
        if (user.UdrUsersPermissions && user.UdrUsersPermissions.length) {
            const target = this.normalizeRole(roleKey);
            return user.UdrUsersPermissions.some((item) => {
                const role = item && item.UdrPermission && item.UdrPermission.role;
                return role && this.normalizeRole(role) === target;
            });
        }

        return false;
    }

    render() {
        const { sendToProviders, saved } = this.props;
        const { type } = this.props;

        // Determine if user can send the actual report based on role + context
        const canSendActual = (type === 'cdpOrthoReports')
            ? this.hasUdrRole('ortho-pay-approval')
            : this.hasUdrRole('general-pay-approval');

        return (
            <div className={'earningSending'}>
                <Modal open={this.state.showSendModal} onClose={this.closeSendModal} size={this.state.noteCheck && (this.state.noteCheck.omitted || []).length ? 'small' : 'tiny'}>
                    <Modal.Header>Send Pay Report</Modal.Header>
                    <Modal.Content>
                        <p>Would you like to send a test email or the actual pay report?</p>
                        {type !== 'cdpOrthoReports' && <>
                            <Button
                                basic size='small' icon labelPosition='left'
                                loading={this.state.checkingNotes}
                                disabled={this.state.checkingNotes}
                                onClick={this.handleCheckOmittedNotes}
                            >
                                <Icon name='search' />
                                Check for omitted notes
                            </Button>
                            {this.renderNoteCheck()}
                        </>}
                    </Modal.Content>
                    <Modal.Actions>
                        <Button onClick={this.closeSendModal}>Cancel</Button>
                        <Button color='blue' onClick={() => this.handleSendReport(true)}>Send Test</Button>
                        {canSendActual && <Button color='green' onClick={() => this.handleSendReport(false)}>Send Actual</Button>}
                    </Modal.Actions>
                </Modal>

                {this.state.success && (
                    <div className="calendar-modal-container">
                        <Modal
                            open={true}
                            closeIcon
                            onClose={() => this.setState({ success: !this.state.success })}
                            header={null}
                            content="Request has been initiated. Selected Providers will get an email shortly!"
                            actions={[{ key: 'ok', content: 'OK', positive: true }]}
                            className="calendar-modal"
                        />
                    </div>
                )}
                <button
                    className={`cdp-filter-toggle${sendToProviders ? ' cdp-filter-toggle--send-live' : ''}`}
                    onClick={this.openSendModal}
                >
                    <i className="send icon" />
                    {sendToProviders ? 'Send Pay Report*' : 'Send Pay Report'}
                </button>
            </div>
        );
    }
}

export default connect(
    state => ({
        user: state.get('auth').get('user'),
    })
)(EarningSending);